import React from 'react';
import { ExternalLink, Hash } from 'lucide-react';

interface LinkifiedTextProps {
  text?: string | null;
  className?: string;
  linkClassName?: string;
  /** Full names of people who can be @mentioned, so "@First Last" is highlighted as one tag */
  mentionNames?: Array<string | null | undefined>;
}

// Combined regex matching:
// 1. URLs (https://..., http://..., www....)
// 2. Task keys like TSK-1001, TASK-104, FE-1, QMS-12, etc. (\b[A-Z]{2,6}-\d{1,6}\b)
// 3. @mentions (\B@[a-zA-Z0-9_.-]+)
const TOKEN_REGEX = /((?:https?:\/\/|www\.)[^\s<>"'`]+|\b[A-Z]{2,6}-\d{1,6}\b|\B@[a-zA-Z0-9_.-]+)/g;

export const LinkifiedText: React.FC<LinkifiedTextProps> = ({
  text,
  className = '',
  linkClassName = 'text-[#026fc1] dark:text-[#38bdf8] underline hover:opacity-80 break-all inline-flex items-center gap-0.5 font-medium transition-opacity',
  mentionNames,
}) => {
  if (!text) return null;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // Full names first (longest wins), so "@Qamar Usman" is one tag instead of "@Qamar" + "Usman"
  const names = Array.from(new Set((mentionNames || []).map((n) => (n || '').trim()).filter(Boolean))).sort(
    (a, b) => b.length - a.length
  );
  const fullNamePattern = names.length
    ? `@(?:${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})(?![a-zA-Z0-9_])|`
    : '';
  // The full-name alternative goes in front of the generic @handle one
  const regex = new RegExp(
    fullNamePattern ? TOKEN_REGEX.source.replace('|\\B@', `|${fullNamePattern}\\B@`) : TOKEN_REGEX.source,
    'g'
  );
  while ((match = regex.exec(text)) !== null) {
    const start = match.index;
    const rawToken = match[0];

    if (start > lastIndex) {
      parts.push(text.slice(lastIndex, start));
    }

    // 1. Check if URL
    if (/^(https?:\/\/|www\.)/i.test(rawToken)) {
      let cleanUrl = rawToken;
      let trailingChars = '';
      while (/[.,;:!?)\]]$/.test(cleanUrl)) {
        trailingChars = cleanUrl.slice(-1) + trailingChars;
        cleanUrl = cleanUrl.slice(0, -1);
      }
      const href = cleanUrl.toLowerCase().startsWith('www.') ? `https://${cleanUrl}` : cleanUrl;

      parts.push(
        <a
          key={`url-${start}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className={linkClassName}
          title={`Open ${href} in a new tab`}
        >
          <span>{cleanUrl}</span>
          <ExternalLink className="w-3 h-3 shrink-0 inline ml-0.5" />
        </a>
      );
      if (trailingChars) {
        parts.push(trailingChars);
      }
    }
    // 2. Check if Task Key (e.g. TSK-1001, TASK-104, FE-1)
    else if (/^[A-Z]{2,6}-\d{1,6}$/.test(rawToken)) {
      parts.push(
        <button
          key={`task-${start}`}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            window.dispatchEvent(
              new CustomEvent('open-task-by-code', { detail: { code: rawToken } })
            );
          }}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded bg-blue-500/15 hover:bg-blue-500/25 text-[#026fc1] dark:text-[#38bdf8] border border-blue-500/30 text-[11px] font-bold font-mono tracking-tight transition-colors cursor-pointer align-baseline"
          title={`Inspect task ${rawToken}`}
        >
          <Hash className="w-2.5 h-2.5 shrink-0" />
          <span>{rawToken}</span>
        </button>
      );
    }
    // 3. Check if @mention
    else if (rawToken.startsWith('@')) {
      parts.push(
        <span
          key={`mention-${start}`}
          className="inline-flex items-center px-1.5 py-0.2 rounded bg-indigo-500/15 text-indigo-600 dark:text-indigo-300 font-semibold text-[0.92em]"
        >
          {rawToken}
        </span>
      );
    } else {
      parts.push(rawToken);
    }

    lastIndex = start + rawToken.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return <span className={className}>{parts}</span>;
};

export default LinkifiedText;
